import { GraphQLQuery } from '@aws-amplify/api';
import { billingClient } from '../../billing-api-integration/client';
import { listProviderMappingsQuery } from './queries';
import { createProviderMappingMutation } from './mutations';
import {
    CreateProviderMappingInput,
    ListProviderMappingsResponse,
    ProviderMapping
} from '../types';

export const listProviderMappings = async (filter?: {
    localEntityId?: string;
    billingProviderId?: string;
    entityType?: string;
}) => {
    const response = await billingClient.graphql<GraphQLQuery<ListProviderMappingsResponse>>({
        query: listProviderMappingsQuery,
        variables: { filter }
    });
    return response.data?.listProviderMappings?.items || [];
};

export const fetchProviderMappings = listProviderMappings;

// FNP-734: a ProviderMapping's `billingProviderId` is the CANONICAL provider KEY-string
// (e.g. 'stripe'), NOT the catalog UUID — a locked backend decision (FNP-664: the mapping is
// keyed on the provider key; FNP-419 deriveProviderMappingId hashes billingProviderId, so it
// must be the stable key). The portal reads a provider from the catalog as {id: <uuid>, key}.
// So "is this provider enabled for the customer?" must compare the mapping's billingProviderId
// to the provider's KEY. We keep the UUID / resolved-nested comparisons as fallbacks for legacy
// rows that stored the UUID or that resolve `billingProvider`. Matching only by UUID (the bug)
// made a genuinely-enabled provider read as Disabled and never self-heal on reload.
export const providerMappingMatchesProvider = (
    mapping: Pick<ProviderMapping, 'billingProviderId' | 'billingProvider'>,
    provider: { id?: string; key?: string },
): boolean => {
    const {id, key} = provider;
    return (
        (!!key && mapping.billingProviderId === key) ||            // canonical: key-string (FNP-664)
        (!!id && mapping.billingProviderId === id) ||             // legacy: UUID-stored rows
        (!!id && mapping.billingProvider?.id === id) ||           // resolved nested by id
        (!!key && mapping.billingProvider?.key === key)           // resolved nested by key
    );
};

// FNP-741: a provider is ENABLED for the customer only when a mapping exists AND its
// linkage-status is 'active'. Disable now RETAINS the mapping and marks it 'inactive'
// (so re-enable reuses the same Stripe cus_ — no duplicate customer), which means the old
// "mapping-presence == enabled" read is wrong: an inactive mapping is still present but the
// provider is OFF. This is the SEPARATE concern from providerMappingMatchesProvider (which
// answers "is this mapping for this provider?") — identity-match first, then active-check.
//
// Strict: only status === 'active' counts as enabled; an absent/null status reads as NOT
// active (product decision, FNP-741 — verified in dev that all live mappings carry an explicit
// status, so no currently-enabled customer regresses).
export const providerMappingIsActive = (
    mapping: Pick<ProviderMapping, 'status'>,
): boolean => mapping.status === 'active';

type CreateProviderMappingResponse = { createProviderMapping: ProviderMapping };

export const createProviderMapping = async (input: CreateProviderMappingInput) => {
    const response = await billingClient.graphql<GraphQLQuery<CreateProviderMappingResponse>>({
        query: createProviderMappingMutation,
        variables: { input }
    });
    return response.data?.createProviderMapping as ProviderMapping;
};

export const getStripeCustomerIdForCustomer = async (customerId: string): Promise<string | null> => {
    console.log('getStripeCustomerIdForCustomer called with:', customerId);
    const mappings = await listProviderMappings({
        localEntityId: customerId,
        entityType: 'Customer'
    });
    
    console.log('All mappings found:', mappings);
    
    if (mappings && mappings.length > 0) {
        // Check if billingProviderId is 'stripe' (string) or if billingProvider.key is 'stripe'
        const stripeMapping = mappings.find((m: ProviderMapping) => {
            console.log('Checking mapping:', m);
            return m.billingProviderId === 'stripe' || m.billingProvider?.key === 'stripe';
        });
        console.log('Stripe mapping found:', stripeMapping);
        return stripeMapping?.providerEntityId || null;
    }
    
    console.log('No mappings found');
    return null;
};

export const getPayPalCustomerIdForCustomer = async (customerId: string): Promise<string | null> => {
    console.log('getPayPalCustomerIdForCustomer called with:', customerId);
    const mappings = await listProviderMappings({
        localEntityId: customerId,
        entityType: 'Customer'
    });
    
    console.log('All mappings found:', mappings);
    
    if (mappings && mappings.length > 0) {
        const paypalMapping = mappings.find((m: ProviderMapping) => {
            console.log('Checking mapping:', m);
            return m.billingProviderId === 'paypal' || m.billingProvider?.key === 'paypal';
        });
        console.log('PayPal mapping found:', paypalMapping);
        return paypalMapping?.providerEntityId || null;
    }
    
    console.log('No mappings found');
    return null;
};
